import type { Estimation } from "@/interfaces/estimation.interface";
import { useState, useMemo } from "react";

interface IssueRateTableProps {
  estimations: Estimation[];
}

type SortField =
  | "projectName"
  | "dateCreated"
  | "issueRate"
  | "updatedIssueRate"
  | "numberOfIssues"
  | "issuesResolved"
  | "falsePositivesDetected"
  | "totalNumberOfLineItems"
  | "processingTime"
  | "falsePositiveRate"
  | "confidenceImproved"
  | "fileName";
type SortDirection = "asc" | "desc";

export const IssueRateTable = ({ estimations }: IssueRateTableProps) => {
  const [dateFilter, setDateFilter] = useState<string>("");
  const [sortField, setSortField] = useState<SortField>("dateCreated");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortValue = (estimation: Estimation, field: SortField) => {
    switch (field) {
      case "projectName":
        return estimation.projectName?.toLowerCase() || "";
      case "dateCreated":
        return new Date(estimation.dateCreated).getTime();
      case "issueRate":
        return estimation.analytics?.issueRate || 0;
      case "updatedIssueRate":
        return estimation.analyticsPostProcessing && estimation.analytics
          ? (estimation.analyticsPostProcessing.updatedIssueCount / estimation.analytics.totalNumberOfLineItems) * 100
          : -1; // Put N/A values at the end
      case "numberOfIssues":
        return estimation.analytics?.numberOfIssues || 0;
      case "issuesResolved":
        return estimation.analyticsPostProcessing?.issuesResolved || -1;
      case "falsePositivesDetected":
        return estimation.analyticsPostProcessing?.falsePositivesDetected || -1;
      case "totalNumberOfLineItems":
        return estimation.analytics?.totalNumberOfLineItems || 0;
      case "processingTime":
        return estimation.analyticsPostProcessing?.processingTimeMs || -1;
      case "falsePositiveRate":
        return estimation.analyticsPostProcessing?.falsePositiveRate || -1;
      case "confidenceImproved":
        return estimation.analyticsPostProcessing?.confidenceImproved || -1;
      case "fileName":
        return (
          estimation.files?.[0]?.originalname?.toLowerCase() ||
          estimation.uploadedFilesProperties?.[0]?.originalFileName?.toLowerCase() ||
          ""
        );
      default:
        return 0;
    }
  };

  const filteredEstimations = useMemo(() => {
    const filtered = estimations
      .filter((estimation) => estimation.analytics)
      .filter((estimation) => {
        if (!dateFilter) return true;
        return estimation.dateCreated === dateFilter;
      });

    return filtered.sort((a, b) => {
      const aValue = getSortValue(a, sortField);
      const bValue = getSortValue(b, sortField);

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [estimations, dateFilter, sortField, sortDirection]);

  const uniqueDates = useMemo(() => {
    return [...new Set(estimations.filter((estimation) => estimation.analytics).map((estimation) => estimation.dateCreated))].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );
  }, [estimations]);

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <th
      scope="col"
      className="cursor-pointer px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase select-none hover:bg-gray-100"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center justify-between">
        {children}
        <div className="ml-1 flex flex-col">
          <span className={`text-xs ${sortField === field && sortDirection === "asc" ? "text-gray-700" : "text-gray-300"}`}>▲</span>
          <span className={`text-xs ${sortField === field && sortDirection === "desc" ? "text-gray-700" : "text-gray-300"}`}>▼</span>
        </div>
      </div>
    </th>
  );

  return (
    <div className="mt-8">
      <div className="mb-4">
        <label htmlFor="dateFilter" className="mb-1 block text-sm font-medium text-gray-700">
          Filter by Date
        </label>
        <select
          id="dateFilter"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="">All Dates</option>
          {uniqueDates.map((date) => (
            <option key={date} value={date}>
              {new Date(date).toLocaleDateString()}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="max-h-[1000px] max-w-screen overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <SortableHeader field="projectName">Project Name</SortableHeader>
                <SortableHeader field="fileName">File Name</SortableHeader>
                <SortableHeader field="dateCreated">Date Created</SortableHeader>
                <SortableHeader field="issueRate">Issue Rate</SortableHeader>
                <SortableHeader field="updatedIssueRate">Updated Issue Rate</SortableHeader>
                <SortableHeader field="numberOfIssues">Number of Issues</SortableHeader>
                <SortableHeader field="issuesResolved">Issues Resolved</SortableHeader>
                <SortableHeader field="falsePositivesDetected">False Positives Detected</SortableHeader>
                <SortableHeader field="totalNumberOfLineItems">Line Items</SortableHeader>
                <SortableHeader field="processingTime">Processing Time (s)</SortableHeader>
                <SortableHeader field="falsePositiveRate">False Positive Rate</SortableHeader>
                <SortableHeader field="confidenceImproved">Confidence Improved Rate</SortableHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredEstimations.map((estimation) => {
                const updatedIssueRate =
                  estimation.analyticsPostProcessing && estimation.analytics
                    ? ((estimation.analyticsPostProcessing.updatedIssueCount / estimation.analytics.totalNumberOfLineItems) * 100).toFixed(
                        2,
                      )
                    : "N/A";

                return (
                  <tr key={estimation.estimateId}>
                    <td className="min-w-[200px] px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                      {estimation.projectName}
                    </td>
                    <td className="min-w-[200px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.files?.[0]?.originalname || estimation.uploadedFilesProperties?.[0]?.originalFileName || "N/A"}
                    </td>
                    <td className="min-w-[120px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {new Date(estimation.dateCreated).toLocaleDateString()}
                    </td>
                    <td className="min-w-[100px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.analytics?.issueRate.toFixed(2)}%
                    </td>
                    <td className="min-w-[130px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {updatedIssueRate !== "N/A" ? `${updatedIssueRate}%` : "N/A"}
                    </td>
                    <td className="min-w-[120px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.analytics?.numberOfIssues}
                    </td>
                    <td className="min-w-[120px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.analyticsPostProcessing?.issuesResolved ?? "N/A"}
                    </td>
                    <td className="min-w-[150px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.analyticsPostProcessing?.falsePositivesDetected ?? "N/A"}
                    </td>
                    <td className="min-w-[100px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.analytics?.totalNumberOfLineItems}
                    </td>
                    <td className="min-w-[130px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.analyticsPostProcessing?.processingTimeMs
                        ? (estimation.analyticsPostProcessing.processingTimeMs / 1000).toFixed(2)
                        : "N/A"}
                    </td>
                    <td className="min-w-[140px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.analyticsPostProcessing?.falsePositiveRate
                        ? `${estimation.analyticsPostProcessing.falsePositiveRate.toFixed(2)}%`
                        : "N/A"}
                    </td>
                    <td className="min-w-[160px] px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {estimation.analyticsPostProcessing?.confidenceImproved ?? "N/A"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
